import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  alpha,
  useMediaQuery,
  useTheme
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';

import { useAppUser } from '@/contexts/AppUserProvider';
import { useHeaderContext } from '@/contexts/HeaderContext';
import { useToast } from '@/components/Toast';
import { listAllCompanies } from '@/services/companyService';
import { Company } from '@/types/companyTypes';
import {
  getCollectionsByCompany,
  updateCollectionsCustomerAccess
} from '@/services/sessionCollectionService';
import { SessionCollection } from '@/types/sessionCollectionTypes';
import {
  CompanySmsEndpoint,
  CustomerAgentDealer,
  CustomerAgentAuthorizedContact,
  CustomerAgentEnrollmentCode,
  UpsertCompanySmsEndpointInput,
  UpsertCustomerAgentDealerInput,
  UpsertCustomerAgentAuthorizedContactInput,
  UpsertCustomerAgentEnrollmentCodeInput,
  getCustomerAgentDealers,
  getCompanySmsEndpoints,
  getCustomerAgentAuthorizedContacts,
  getCustomerAgentEnrollmentCodes,
  revokeCustomerAgentDealer,
  revokeCompanySmsEndpoint,
  revokeCustomerAgentAuthorizedContact,
  revokeCustomerAgentEnrollmentCode,
  upsertCustomerAgentDealer,
  upsertCompanySmsEndpoint,
  upsertCustomerAgentAuthorizedContact,
  upsertCustomerAgentEnrollmentCode
} from '@/services/customerAgentAdminService';

const emptyEndpointForm: UpsertCompanySmsEndpointInput = {
  phoneNumber: '',
  displayName: '',
  description: '',
  isPrimary: false,
  isActive: true,
  twilioPhoneNumberSid: '',
  linkedDealerIds: [],
  endpointType: 0
};

const emptyDealerForm: UpsertCustomerAgentDealerInput = {
  id: null,
  name: '',
  description: '',
  isActive: true,
  isDefaultOnboardingDealer: false
};

const emptyContactForm: UpsertCustomerAgentAuthorizedContactInput = {
  id: null,
  dealerId: '',
  phoneNumber: '',
  displayName: '',
  description: '',
  accessExpiresAt: null
};

const emptyEnrollmentCodeForm: UpsertCustomerAgentEnrollmentCodeInput = {
  id: null,
  dealerId: '',
  code: '',
  displayName: '',
  isActive: true,
  expiresAt: null,
  maxUses: null
};

const statusChip = (isActive: boolean) => (
  <Chip
    size="small"
    label={isActive ? 'Active' : 'Inactive'}
    color={isActive ? 'success' : 'default'}
    variant={isActive ? 'filled' : 'outlined'}
  />
);

export const AdminSmsPage: React.FC = () => {
  const { appUser, selectedUserRole } = useAppUser();
  const { getAccessTokenSilently, getAccessTokenWithPopup } = useAuth0();
  const { setHeaderText, setMenuActions } = useHeaderContext();
  const { showError, showSuccess } = useToast();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  const [dealers, setDealers] = useState<CustomerAgentDealer[]>([]);
  const [selectedDealerId, setSelectedDealerId] = useState<string>('');
  const [endpoints, setEndpoints] = useState<CompanySmsEndpoint[]>([]);
  const [contacts, setContacts] = useState<CustomerAgentAuthorizedContact[]>([]);
  const [enrollmentCodes, setEnrollmentCodes] = useState<CustomerAgentEnrollmentCode[]>([]);
  const [collections, setCollections] = useState<SessionCollection[]>([]);
  const [selectedCollectionIds, setSelectedCollectionIds] = useState<string[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingFolders, setSavingFolders] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [endpointDialogOpen, setEndpointDialogOpen] = useState(false);
  const [endpointForm, setEndpointForm] =
    useState<UpsertCompanySmsEndpointInput>(emptyEndpointForm);

  const [dealerDialogOpen, setDealerDialogOpen] = useState(false);
  const [dealerForm, setDealerForm] =
    useState<UpsertCustomerAgentDealerInput>(emptyDealerForm);

  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [contactForm, setContactForm] =
    useState<UpsertCustomerAgentAuthorizedContactInput>(emptyContactForm);
  const [editingContactId, setEditingContactId] = useState<string | null>(null);

  const [codeDialogOpen, setCodeDialogOpen] = useState(false);
  const [codeForm, setCodeForm] =
    useState<UpsertCustomerAgentEnrollmentCodeInput>(emptyEnrollmentCodeForm);

  const selectedCompany = useMemo(
    () => companies.find((company) => company.id === selectedCompanyId),
    [companies, selectedCompanyId]
  );

  const selectedDealer = useMemo(
    () => dealers.find((dealer) => dealer.id === selectedDealerId),
    [dealers, selectedDealerId]
  );

  const filteredContacts = useMemo(
    () =>
      selectedDealerId
        ? contacts.filter((contact) => contact.dealerId === selectedDealerId)
        : contacts,
    [contacts, selectedDealerId]
  );

  const filteredEnrollmentCodes = useMemo(
    () =>
      selectedDealerId
        ? enrollmentCodes.filter((code) => code.dealerId === selectedDealerId)
        : enrollmentCodes,
    [enrollmentCodes, selectedDealerId]
  );

  const customerSearchFolderCount = useMemo(
    () => collections.filter((collection) => collection.exposeToCustomerAgent).length,
    [collections]
  );

  const allCollectionsSelected =
    collections.length > 0 && selectedCollectionIds.length === collections.length;
  const someCollectionsSelected =
    selectedCollectionIds.length > 0 && selectedCollectionIds.length < collections.length;

  const userId = appUser?.id;

  const loadCompanyData = useCallback(
    async (companyId: string) => {
      if (!companyId || !userId) return;
      setLoadingData(true);
      setError(null);
      try {
        const [dealerRows, endpointRows, contactRows, codeRows, collectionRows] = await Promise.all([
          getCustomerAgentDealers(
            companyId,
            userId,
            getAccessTokenSilently,
            getAccessTokenWithPopup
          ),
          getCompanySmsEndpoints(
            companyId,
            userId,
            getAccessTokenSilently,
            getAccessTokenWithPopup
          ),
          getCustomerAgentAuthorizedContacts(
            companyId,
            userId,
            getAccessTokenSilently,
            getAccessTokenWithPopup
          ),
          getCustomerAgentEnrollmentCodes(
            companyId,
            userId,
            getAccessTokenSilently,
            getAccessTokenWithPopup
          ),
          getCollectionsByCompany(
            companyId,
            userId,
            getAccessTokenSilently,
            getAccessTokenWithPopup,
            1,
            1000
          )
        ]);
        setDealers(dealerRows);
        setEndpoints(endpointRows);
        setContacts(contactRows);
        setEnrollmentCodes(codeRows);
        setCollections(collectionRows);
        setSelectedCollectionIds([]);
        setSelectedDealerId((prev) => {
          if (prev && dealerRows.some((dealer) => dealer.id === prev)) {
            return prev;
          }

          return (
            dealerRows.find((dealer) => dealer.isDefaultOnboardingDealer)?.id ??
            dealerRows[0]?.id ??
            ''
          );
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load SMS settings';
        setError(message);
        showError(message);
      } finally {
        setLoadingData(false);
      }
    },
    [getAccessTokenSilently, getAccessTokenWithPopup, showError, userId]
  );

  useEffect(() => {
    setHeaderText('Admin');
    setMenuActions(null);
    return () => setMenuActions(null);
  }, [setHeaderText, setMenuActions]);

  useEffect(() => {
    if (!appUser?.isSuperAdmin || !userId) return;

    const loadCompanies = async () => {
      setLoadingCompanies(true);
      setError(null);
      try {
        const rows = await listAllCompanies(
          { pageNumber: 1, pageSize: 100 },
          userId,
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );
        setCompanies(rows);
        const preferredCompanyId =
          selectedUserRole?.companyId && rows.some((company) => company.id === selectedUserRole.companyId)
            ? selectedUserRole.companyId
            : rows[0]?.id ?? '';
        setSelectedCompanyId(preferredCompanyId);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load companies';
        setError(message);
        showError(message);
      } finally {
        setLoadingCompanies(false);
      }
    };

    loadCompanies();
  }, [
    appUser?.isSuperAdmin,
    getAccessTokenSilently,
    getAccessTokenWithPopup,
    selectedUserRole?.companyId,
    showError,
    userId
  ]);

  useEffect(() => {
    if (selectedCompanyId) {
      loadCompanyData(selectedCompanyId);
    }
  }, [loadCompanyData, selectedCompanyId]);

  const openEndpointDialog = (endpoint?: CompanySmsEndpoint) => {
    setEndpointForm(
      endpoint
        ? {
            id: endpoint.id,
            phoneNumber: endpoint.phoneNumber,
            displayName: endpoint.displayName ?? '',
            description: endpoint.description ?? '',
            isPrimary: endpoint.isPrimary,
            isActive: endpoint.isActive,
            twilioPhoneNumberSid: endpoint.twilioPhoneNumberSid ?? '',
            linkedDealerIds: endpoint.linkedDealerIds,
            endpointType: endpoint.endpointType
          }
        : emptyEndpointForm
    );
    setEndpointDialogOpen(true);
  };

  const openDealerDialog = (dealer?: CustomerAgentDealer) => {
    setDealerForm(
      dealer
        ? {
            id: dealer.id,
            name: dealer.name,
            description: dealer.description ?? '',
            isActive: dealer.isActive,
            isDefaultOnboardingDealer: dealer.isDefaultOnboardingDealer
          }
        : emptyDealerForm
    );
    setDealerDialogOpen(true);
  };

  const openContactDialog = (contact?: CustomerAgentAuthorizedContact) => {
    setEditingContactId(contact?.id ?? null);
    setContactForm(
      contact
        ? {
            id: contact.id,
            dealerId: contact.dealerId,
            phoneNumber: contact.phoneNumber,
            displayName: contact.displayName ?? '',
            description: contact.description ?? '',
            accessExpiresAt: contact.accessExpiresAt ?? null
          }
        : { ...emptyContactForm, dealerId: selectedDealerId }
    );
    setContactDialogOpen(true);
  };

  const openCodeDialog = (code?: CustomerAgentEnrollmentCode) => {
    setCodeForm(
      code
        ? {
            id: code.id,
            dealerId: code.dealerId,
            code: code.code,
            displayName: code.displayName ?? '',
            isActive: code.isActive,
            expiresAt: code.expiresAt ?? null,
            maxUses: code.maxUses ?? null
          }
        : { ...emptyEnrollmentCodeForm, dealerId: selectedDealerId }
    );
    setCodeDialogOpen(true);
  };

  const handleSaveEndpoint = async () => {
    if (!selectedCompanyId || !userId) return;
    setSaving(true);
    try {
      await upsertCompanySmsEndpoint(
        selectedCompanyId,
        endpointForm,
        userId,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );
      setEndpointDialogOpen(false);
      showSuccess('SMS endpoint saved');
      await loadCompanyData(selectedCompanyId);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to save SMS endpoint');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDealer = async () => {
    if (!selectedCompanyId || !userId) return;
    setSaving(true);
    try {
      const dealer = await upsertCustomerAgentDealer(
        selectedCompanyId,
        dealerForm,
        userId,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );
      setDealerDialogOpen(false);
      setSelectedDealerId(dealer.id);
      showSuccess('Dealer saved');
      await loadCompanyData(selectedCompanyId);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to save dealer');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDealer = async (dealer: CustomerAgentDealer) => {
    if (!selectedCompanyId || !userId) return;
    if (!window.confirm(`Remove ${dealer.name}?`)) return;
    try {
      await revokeCustomerAgentDealer(
        selectedCompanyId,
        dealer.id,
        userId,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );
      showSuccess('Dealer removed');
      if (selectedDealerId === dealer.id) {
        setSelectedDealerId('');
      }
      await loadCompanyData(selectedCompanyId);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to remove dealer');
    }
  };

  const handleDeleteEndpoint = async (endpoint: CompanySmsEndpoint) => {
    if (!selectedCompanyId || !userId) return;
    if (!window.confirm(`Remove ${endpoint.displayName || endpoint.phoneNumber}?`)) return;
    try {
      await revokeCompanySmsEndpoint(
        selectedCompanyId,
        endpoint.id,
        userId,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );
      showSuccess('SMS endpoint removed');
      await loadCompanyData(selectedCompanyId);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to remove SMS endpoint');
    }
  };

  const handleSaveContact = async () => {
    if (!selectedCompanyId || !userId) return;
    setSaving(true);
    try {
      await upsertCustomerAgentAuthorizedContact(
        selectedCompanyId,
        contactForm,
        userId,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );
      setContactDialogOpen(false);
      setEditingContactId(null);
      showSuccess('Authorized contact saved');
      await loadCompanyData(selectedCompanyId);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to save contact');
    } finally {
      setSaving(false);
    }
  };

  const handleRevokeContact = async (contact: CustomerAgentAuthorizedContact) => {
    if (!selectedCompanyId || !userId) return;
    if (!window.confirm(`Revoke ${contact.displayName || contact.phoneNumber}?`)) return;
    try {
      await revokeCustomerAgentAuthorizedContact(
        selectedCompanyId,
        contact.id,
        userId,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );
      showSuccess('Authorized contact revoked');
      await loadCompanyData(selectedCompanyId);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to revoke contact');
    }
  };

  const handleSaveCode = async () => {
    if (!selectedCompanyId || !userId) return;
    setSaving(true);
    try {
      await upsertCustomerAgentEnrollmentCode(
        selectedCompanyId,
        {
          ...codeForm,
          maxUses: codeForm.maxUses ? Number(codeForm.maxUses) : null
        },
        userId,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );
      setCodeDialogOpen(false);
      showSuccess('Enrollment code saved');
      await loadCompanyData(selectedCompanyId);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to save enrollment code');
    } finally {
      setSaving(false);
    }
  };

  const handleRevokeCode = async (code: CustomerAgentEnrollmentCode) => {
    if (!selectedCompanyId || !userId) return;
    if (!window.confirm(`Deactivate ${code.displayName || code.code}?`)) return;
    try {
      await revokeCustomerAgentEnrollmentCode(
        selectedCompanyId,
        code.id,
        userId,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );
      showSuccess('Enrollment code deactivated');
      await loadCompanyData(selectedCompanyId);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to deactivate enrollment code');
    }
  };

  const handleToggleCollectionSelection = (collectionId: string) => {
    setSelectedCollectionIds((prev) =>
      prev.includes(collectionId)
        ? prev.filter((id) => id !== collectionId)
        : [...prev, collectionId]
    );
  };

  const handleToggleAllCollections = (checked: boolean) => {
    setSelectedCollectionIds(
      checked ? collections.map((collection) => collection.id) : []
    );
  };

  const handleBulkFolderAccessChange = async (exposeToCustomerAgent: boolean) => {
    if (!selectedCompanyId || !userId || selectedCollectionIds.length === 0) return;

    const folderIds = [...selectedCollectionIds];
    setSavingFolders(true);
    try {
      await updateCollectionsCustomerAccess(
        folderIds,
        exposeToCustomerAgent,
        userId,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );
      showSuccess(
        `${folderIds.length} folder${folderIds.length === 1 ? '' : 's'} ${
          exposeToCustomerAgent ? 'enabled' : 'disabled'
        } for customer search`
      );
      await loadCompanyData(selectedCompanyId);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to update folders');
    } finally {
      setSavingFolders(false);
    }
  };

  if (!appUser?.isSuperAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Super admin access is required.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1320, mx: 'auto' }}>
      <Stack spacing={3}>
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'stretch', md: 'center' },
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2
          }}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              SMS customer agent admin
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Configure tenant Twilio numbers and the customer phone numbers allowed to message them.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 280 } }}>
              <InputLabel>Company</InputLabel>
              <Select
                label="Company"
                value={selectedCompanyId}
                onChange={(event) => setSelectedCompanyId(event.target.value)}
                disabled={loadingCompanies}
              >
                {companies.map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 280 } }}>
              <InputLabel>Dealer</InputLabel>
              <Select
                label="Dealer"
                value={selectedDealerId}
                onChange={(event) => setSelectedDealerId(event.target.value)}
                disabled={!selectedCompanyId || dealers.length === 0}
              >
                {dealers.map((dealer) => (
                  <MenuItem key={dealer.id} value={dealer.id}>
                    {dealer.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              size="small"
              startIcon={<AddIcon />}
              variant="outlined"
              onClick={() => openDealerDialog()}
              disabled={!selectedCompanyId}
              sx={{ flexShrink: 0 }}
            >
              Add dealer
            </Button>
            <IconButton
              aria-label="Refresh SMS settings"
              onClick={() => selectedCompanyId && loadCompanyData(selectedCompanyId)}
              disabled={!selectedCompanyId || loadingData}
            >
              <RefreshIcon />
            </IconButton>
          </Stack>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1.1fr) minmax(360px, 0.9fr)',
            gap: 2
          }}
        >
          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <SectionHeader
              icon={<SmsOutlinedIcon fontSize="small" />}
              title="SMS endpoints"
              subtitle={selectedCompany ? selectedCompany.name : 'Select a company'}
              action={
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  variant="contained"
                  onClick={() => openEndpointDialog()}
                  disabled={!selectedCompanyId}
                >
                  Endpoint
                </Button>
              }
            />
            <Divider />
            {loadingData ? (
              <LoadingBlock />
            ) : endpoints.length === 0 ? (
              <EmptyBlock text="No SMS endpoints configured for this company." />
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Number</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {endpoints.map((endpoint) => (
                    <TableRow key={endpoint.id} hover>
                      <TableCell>
                        <Stack spacing={0.25}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {endpoint.phoneNumber}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {endpoint.normalizedPhoneNumber}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography variant="body2">
                            {endpoint.displayName || 'Unnamed endpoint'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {endpoint.linkedDealerNames.length > 0
                              ? endpoint.linkedDealerNames.join(', ')
                              : 'No linked dealers'}
                          </Typography>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap">
                            {endpoint.isPrimary && (
                              <Chip size="small" label="Primary" variant="outlined" />
                            )}
                          </Stack>
                        </Stack>
                      </TableCell>
                      <TableCell>{statusChip(endpoint.isActive)}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => openEndpointDialog(endpoint)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteEndpoint(endpoint)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>

          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <SectionHeader
              icon={<StorefrontOutlinedIcon fontSize="small" />}
              title="Dealers"
              subtitle="Shared inbox routing partitions for this company"
              action={
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  variant="contained"
                  onClick={() => openDealerDialog()}
                  disabled={!selectedCompanyId}
                >
                  Dealer
                </Button>
              }
            />
            <Divider />
            {loadingData ? (
              <LoadingBlock />
            ) : dealers.length === 0 ? (
              <EmptyBlock text="No dealers configured for this company." />
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Dealer</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dealers.map((dealer) => (
                    <TableRow
                      key={dealer.id}
                      hover
                      selected={dealer.id === selectedDealerId}
                      onClick={() => setSelectedDealerId(dealer.id)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Stack spacing={0.25}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {dealer.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {dealer.linkedEndpointPhoneNumbers.length > 0
                              ? dealer.linkedEndpointPhoneNumbers.join(', ')
                              : 'No linked endpoints'}
                          </Typography>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap">
                            {dealer.isDefaultOnboardingDealer && (
                              <Chip
                                size="small"
                                label="Default onboarding dealer"
                                color="secondary"
                                variant="outlined"
                              />
                            )}
                          </Stack>
                        </Stack>
                      </TableCell>
                      <TableCell>{statusChip(dealer.isActive)}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => openDealerDialog(dealer)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteDealer(dealer)}
                          disabled={!dealer.isActive}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>
        </Box>

        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <SectionHeader
            icon={<PersonAddAltOutlinedIcon fontSize="small" />}
            title="Authorized contacts"
            subtitle={
              selectedDealer
                ? `Phone numbers allowed to use ${selectedDealer.name}`
                : 'Select a dealer to manage authorized contacts'
            }
            action={
              <Button
                size="small"
                startIcon={<AddIcon />}
                variant="contained"
                onClick={() => openContactDialog()}
                disabled={!selectedCompanyId || !selectedDealerId}
              >
                Contact
              </Button>
            }
          />
          <Divider />
          {loadingData ? (
            <LoadingBlock />
          ) : !selectedDealerId ? (
            <EmptyBlock text="Select a dealer to view authorized contacts." />
          ) : filteredContacts.length === 0 ? (
            <EmptyBlock text="No customer phone numbers are authorized for this dealer yet." />
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Contact</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredContacts.map((contact) => (
                  <TableRow key={contact.id} hover>
                    <TableCell>
                      <Stack spacing={0.25}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {contact.displayName || contact.phoneNumber}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {contact.normalizedPhoneNumber}
                        </Typography>
                        {contact.accessExpiresAt && (
                          <Typography variant="caption" color="text.secondary">
                            Expires {formatTimestamp(contact.accessExpiresAt)}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>{statusChip(contact.isActive)}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => openContactDialog(contact)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRevokeContact(contact)}
                        disabled={!contact.isActive}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>

        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <SectionHeader
            icon={<FolderOutlinedIcon fontSize="small" />}
            title="Customer-searchable folders"
            subtitle={`${customerSearchFolderCount} of ${collections.length} folders enabled`}
            action={
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ xs: 'stretch', sm: 'center' }}
              >
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => handleBulkFolderAccessChange(true)}
                  disabled={
                    !selectedCompanyId ||
                    selectedCollectionIds.length === 0 ||
                    savingFolders
                  }
                >
                  Enable selected
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleBulkFolderAccessChange(false)}
                  disabled={
                    !selectedCompanyId ||
                    selectedCollectionIds.length === 0 ||
                    savingFolders
                  }
                >
                  Disable selected
                </Button>
              </Stack>
            }
          />
          <Divider />
          {loadingData ? (
            <LoadingBlock />
          ) : collections.length === 0 ? (
            <EmptyBlock text="No folders found for this company." />
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 720 }}>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={allCollectionsSelected}
                        indeterminate={someCollectionsSelected}
                        onChange={(event) =>
                          handleToggleAllCollections(event.target.checked)
                        }
                        disabled={savingFolders}
                      />
                    </TableCell>
                    <TableCell>Folder</TableCell>
                    <TableCell>Customer search</TableCell>
                    <TableCell>Records</TableCell>
                    <TableCell>Documents</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {collections.map((collection) => {
                    const isSelected = selectedCollectionIds.includes(collection.id);
                    return (
                      <TableRow key={collection.id} hover selected={isSelected}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={isSelected}
                            onChange={() => handleToggleCollectionSelection(collection.id)}
                            disabled={savingFolders}
                          />
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.25} minWidth={0}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600 }}
                              noWrap
                            >
                              {collection.title}
                            </Typography>
                            {collection.referenceNumber && (
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {collection.referenceNumber}
                              </Typography>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={
                              collection.exposeToCustomerAgent
                                ? 'Enabled'
                                : 'Disabled'
                            }
                            color={
                              collection.exposeToCustomerAgent
                                ? 'success'
                                : 'default'
                            }
                            variant={
                              collection.exposeToCustomerAgent
                                ? 'filled'
                                : 'outlined'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {collection.sessionCount}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {collection.documentCount}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </Paper>

        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <SectionHeader
            icon={<VpnKeyOutlinedIcon fontSize="small" />}
            title="Enrollment codes"
            subtitle={
              selectedDealer
                ? `Codes customers can text to join ${selectedDealer.name}`
                : 'Select a dealer to manage enrollment codes'
            }
            action={
              <Button
                size="small"
                startIcon={<AddIcon />}
                variant="contained"
                onClick={() => openCodeDialog()}
                disabled={!selectedCompanyId || !selectedDealerId}
              >
                Code
              </Button>
            }
          />
          <Divider />
          {loadingData ? (
            <LoadingBlock />
          ) : !selectedDealerId ? (
            <EmptyBlock text="Select a dealer to view enrollment codes." />
          ) : filteredEnrollmentCodes.length === 0 ? (
            <EmptyBlock text="No enrollment codes configured for this dealer." />
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Dealer</TableCell>
                  <TableCell>Usage</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredEnrollmentCodes.map((code) => (
                  <TableRow key={code.id} hover>
                    <TableCell>
                      <Stack spacing={0.25}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {code.code}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {code.displayName || code.normalizedCode}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {code.dealerName || 'Unknown dealer'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {code.useCount}
                        {code.maxUses ? ` / ${code.maxUses}` : ''}
                      </Typography>
                    </TableCell>
                    <TableCell>{statusChip(code.isActive)}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => openCodeDialog(code)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRevokeCode(code)}
                        disabled={!code.isActive}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      </Stack>

      <Dialog
        open={endpointDialogOpen}
        onClose={() => setEndpointDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {endpointForm.id ? 'Edit SMS endpoint' : 'Add SMS endpoint'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Twilio phone number"
              value={endpointForm.phoneNumber}
              onChange={(event) =>
                setEndpointForm((prev) => ({ ...prev, phoneNumber: event.target.value }))
              }
              placeholder="+15551234567"
              fullWidth
              required
            />
            <TextField
              label="Display name"
              value={endpointForm.displayName ?? ''}
              onChange={(event) =>
                setEndpointForm((prev) => ({ ...prev, displayName: event.target.value }))
              }
              fullWidth
            />
            <TextField
              label="Description"
              value={endpointForm.description ?? ''}
              onChange={(event) =>
                setEndpointForm((prev) => ({ ...prev, description: event.target.value }))
              }
              fullWidth
              multiline
              minRows={2}
            />
            <FormControl fullWidth>
              <InputLabel>Linked dealers</InputLabel>
              <Select
                multiple
                label="Linked dealers"
                value={endpointForm.linkedDealerIds}
                onChange={(event) =>
                  setEndpointForm((prev) => ({
                    ...prev,
                    linkedDealerIds: (event.target.value as string[]) ?? []
                  }))
                }
                renderValue={(selected) => {
                  const ids = selected as string[];
                  return dealers
                    .filter((dealer) => ids.includes(dealer.id))
                    .map((dealer) => dealer.name)
                    .join(', ');
                }}
              >
                {dealers.map((dealer) => (
                  <MenuItem key={dealer.id} value={dealer.id}>
                    {dealer.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Stack direction="row" spacing={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={endpointForm.isActive}
                    onChange={(event) =>
                      setEndpointForm((prev) => ({ ...prev, isActive: event.target.checked }))
                    }
                  />
                }
                label="Active"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={endpointForm.isPrimary}
                    onChange={(event) =>
                      setEndpointForm((prev) => ({ ...prev, isPrimary: event.target.checked }))
                    }
                  />
                }
                label="Primary"
              />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Link this number to every dealer that can receive customers through it. Temporary first-time access is controlled by the dealer marked as default onboarding.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEndpointDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveEndpoint}
            variant="contained"
            disabled={saving || !endpointForm.phoneNumber.trim()}
          >
            {saving ? <CircularProgress size={18} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dealerDialogOpen}
        onClose={() => setDealerDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{dealerForm.id ? 'Edit dealer' : 'Add dealer'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Dealer name"
              value={dealerForm.name}
              onChange={(event) =>
                setDealerForm((prev) => ({ ...prev, name: event.target.value }))
              }
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={dealerForm.description ?? ''}
              onChange={(event) =>
                setDealerForm((prev) => ({ ...prev, description: event.target.value }))
              }
              fullWidth
              multiline
              minRows={2}
            />
            <Stack direction="row" spacing={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={dealerForm.isActive}
                    onChange={(event) =>
                      setDealerForm((prev) => ({ ...prev, isActive: event.target.checked }))
                    }
                  />
                }
                label="Active"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={dealerForm.isDefaultOnboardingDealer}
                    onChange={(event) =>
                      setDealerForm((prev) => ({
                        ...prev,
                        isDefaultOnboardingDealer: event.target.checked
                      }))
                    }
                  />
                }
                label="Default onboarding dealer"
              />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Use this for the synthetic shared-access dealer that new customers land in before they authenticate to a real dealer.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDealerDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveDealer}
            variant="contained"
            disabled={saving || !dealerForm.name.trim()}
          >
            {saving ? <CircularProgress size={18} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={contactDialogOpen}
        onClose={() => setContactDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{editingContactId ? 'Edit contact' : 'Add authorized contact'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Dealer</InputLabel>
              <Select
                label="Dealer"
                value={contactForm.dealerId}
                onChange={(event) =>
                  setContactForm((prev) => ({ ...prev, dealerId: event.target.value }))
                }
              >
                {dealers.map((dealer) => (
                  <MenuItem key={dealer.id} value={dealer.id}>
                    {dealer.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Customer phone number"
              value={contactForm.phoneNumber}
              onChange={(event) =>
                setContactForm((prev) => ({ ...prev, phoneNumber: event.target.value }))
              }
              placeholder="+15558675309"
              fullWidth
              required
            />
            <TextField
              label="Display name"
              value={contactForm.displayName ?? ''}
              onChange={(event) =>
                setContactForm((prev) => ({ ...prev, displayName: event.target.value }))
              }
              fullWidth
            />
            <TextField
              label="Description"
              value={contactForm.description ?? ''}
              onChange={(event) =>
                setContactForm((prev) => ({ ...prev, description: event.target.value }))
              }
              fullWidth
              multiline
              minRows={2}
            />
            <TextField
              label="Access expires at"
              type="datetime-local"
              value={toLocalDateTimeInputValue(contactForm.accessExpiresAt)}
              onChange={(event) =>
                setContactForm((prev) => ({
                  ...prev,
                  accessExpiresAt: event.target.value
                    ? new Date(event.target.value).toISOString()
                    : null
                }))
              }
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="Leave blank for permanent access."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContactDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveContact}
            variant="contained"
            disabled={saving || !contactForm.dealerId || !contactForm.phoneNumber.trim()}
          >
            {saving ? <CircularProgress size={18} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={codeDialogOpen}
        onClose={() => setCodeDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{codeForm.id ? 'Edit enrollment code' : 'Add enrollment code'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Dealer</InputLabel>
              <Select
                label="Dealer"
                value={codeForm.dealerId}
                onChange={(event) =>
                  setCodeForm((prev) => ({ ...prev, dealerId: event.target.value }))
                }
              >
                {dealers.map((dealer) => (
                  <MenuItem key={dealer.id} value={dealer.id}>
                    {dealer.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Code"
              value={codeForm.code}
              onChange={(event) =>
                setCodeForm((prev) => ({ ...prev, code: event.target.value }))
              }
              placeholder="SMITHFORD"
              fullWidth
              required
            />
            <TextField
              label="Display name"
              value={codeForm.displayName ?? ''}
              onChange={(event) =>
                setCodeForm((prev) => ({ ...prev, displayName: event.target.value }))
              }
              fullWidth
            />
            <TextField
              label="Max uses"
              type="number"
              value={codeForm.maxUses ?? ''}
              onChange={(event) =>
                setCodeForm((prev) => ({
                  ...prev,
                  maxUses: event.target.value ? Number(event.target.value) : null
                }))
              }
              fullWidth
              inputProps={{ min: 1 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={codeForm.isActive}
                  onChange={(event) =>
                    setCodeForm((prev) => ({ ...prev, isActive: event.target.checked }))
                  }
                />
              }
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCodeDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveCode}
            variant="contained"
            disabled={saving || !codeForm.dealerId || !codeForm.code.trim()}
          >
            {saving ? <CircularProgress size={18} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

interface SectionHeaderProps {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  action: React.ReactNode;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({ icon, title, subtitle, action }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        p: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        bgcolor: alpha(theme.palette.primary.main, 0.025)
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" minWidth={0}>
        <Box sx={{ color: 'text.secondary', display: 'flex' }}>{icon}</Box>
        <Box minWidth={0}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {subtitle}
          </Typography>
        </Box>
      </Stack>
      {action}
    </Box>
  );
};

const LoadingBlock = () => (
  <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
    <CircularProgress size={24} />
  </Box>
);

const EmptyBlock = ({ text }: { text: string }) => (
  <Box sx={{ p: 4 }}>
    <Typography variant="body2" color="text.secondary" textAlign="center">
      {text}
    </Typography>
  </Box>
);

const toLocalDateTimeInputValue = (value?: string | null) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const offsetMs = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
};

const formatTimestamp = (value?: string | null) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

export default AdminSmsPage;
